"use client";

import { ClipboardList } from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import { useState } from "react";

import { CardActions, createCardActionsProps } from "@/src/components/cards/card-actions";
import { EntityDropdownMenu, LinkToEntity } from "@/src/components/cards/common";
import { DiagnosisListTable } from "@/src/components/cards/diagnosis-list-table";
import ReferralCardEditable from "@/src/components/cards/referral-card/referral-card-editable";
import { ReferralTagBadges } from "@/src/components/referrals/referral-tag-badges";
import { Badge } from "@/src/components/shadcn/ui/badge";
import { ContextMenu, ContextMenuTrigger } from "@/src/components/shadcn/ui/context-menu";
import {
  referralStatusIcons,
  defaultReferralStatusIconAndColor,
  schedulingPriorityIcons,
} from "@/src/components/tasks/model-icons";
import {
  ValerieCard,
  ValerieCardField,
  ValerieCardSection,
  ValerieCardSectionTitle,
  ValerieCardSections,
} from "@/src/components/valerie-card";
import { useUser } from "@/src/context/user-context";
import { usePriorAuthorizationsByReferralId } from "@/src/lib/data-fetchers/prior-authorization-fetchers";
import { canWriteSOR } from "@/src/lib/rbac";
import { formatFaxParens, formatIsoDate } from "@/src/lib/utils/date-and-phone-utils";
import {
  isStatOrUrgentPriority,
  Referral,
  ReferralStatus,
  SchedulingPriority,
  referralStatusLabel,
} from "@/src/models/documents/referral";
import { getPatientName } from "@/src/models/patient";

export default function ReferralCard({ referral, isEditable = false }: { referral: Referral; isEditable?: boolean }) {
  const [isEditing, setIsEditing] = useState(false);
  const pathname = usePathname();
  const router = useRouter();
  const { roles } = useUser();
  const canWriteSor = canWriteSOR(roles);
  const effectiveIsEditable = isEditable;
  const { priorAuthorizations } = usePriorAuthorizationsByReferralId(referral?.id ?? null);
  const authExpirationDate = priorAuthorizations.find((pa) => pa.endDate)?.endDate ?? null;

  if (!referral) {
    return null;
  }

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  if (isEditing) {
    return <ReferralCardEditable referral={referral} onClose={handleEditToggle} />;
  }

  const patientDisplayId = referral.patient?.displayId;
  const patientName = referral.patient ? getPatientName(referral.patient) : null;

  const customActions: { label: string; onClick: () => void }[] = [
    ...(canWriteSor ? [{ label: "Edit Referral", onClick: handleEditToggle }] : []),
    ...(patientDisplayId
      ? [{ label: "View Patient", onClick: () => router.push(`/patients/${patientDisplayId}`) }]
      : []),
  ];

  const cardActionsProps = createCardActionsProps({
    id: referral.id,
    label: "Referral ID",
    href: `/referrals/${referral.displayId}`,
    customProps: customActions,
  });

  const diagnosisList = Array.isArray(referral.diagnosisList) ? referral.diagnosisList : [];
  const hasDiagnoses = diagnosisList.length > 0 || Boolean(referral.diagnosisCode || referral.diagnosisText);
  const status = referral.status as ReferralStatus | null;
  const statusMeta = referralStatusIcons[status || ReferralStatus.NOT_STARTED] || defaultReferralStatusIconAndColor;
  const StatusIcon = statusMeta?.icon;
  const schedulingPriority = referral.schedulingPriority as SchedulingPriority | null;
  const schedulingPriorityMeta = schedulingPriority ? schedulingPriorityIcons[schedulingPriority] : null;
  const SchedulingPriorityIcon = schedulingPriorityMeta?.icon;
  const showSchedulingPriorityFlag = isStatOrUrgentPriority(schedulingPriority);
  const referringFaxNumber = referral.referringLocation?.faxPhoneNumber;
  const referringFax = referringFaxNumber ? formatFaxParens(referringFaxNumber) : null;

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <ValerieCard
          className="w-full"
          icon={<ClipboardList />}
          title={
            <span className="inline-flex items-center gap-2">
              Referral
              {showSchedulingPriorityFlag && SchedulingPriorityIcon && (
                <SchedulingPriorityIcon weight="fill" className={`h-4 w-4 ${schedulingPriorityMeta?.color ?? ""}`} />
              )}
              {!!referral.section?.reviewedContent?.isVaMilitaryReferral && (
                <Badge className="bg-yellow-500 text-white dark:bg-yellow-600">VA / Military / ADSM</Badge>
              )}
              <ReferralTagBadges tags={referral.tags} />
            </span>
          }
          details={[
            { label: "Patient", value: patientName },
            { label: "Referred", value: referral.dateOfReferral ? formatIsoDate(referral.dateOfReferral) : null },
            { label: "Received", value: referral.dateReceived ? formatIsoDate(referral.dateReceived) : null },
          ]}
          actions={
            <>
              {!pathname?.startsWith(`/referrals/${referral.displayId}`) ? (
                <LinkToEntity href={`/referrals/${referral.displayId}`} />
              ) : null}
              {effectiveIsEditable && <EntityDropdownMenu cardActionsProps={cardActionsProps} />}
            </>
          }
        >
          <ValerieCardSections>
            <ValerieCardSection title="Referral">
              <ValerieCardField label="Status">
                <div className="flex items-center gap-2">
                  {StatusIcon && <StatusIcon weight="fill" className={`h-4 w-4 ${statusMeta?.color ?? ""}`} />}
                  <span className="no-select">{referralStatusLabel(status || ReferralStatus.NOT_STARTED)}</span>
                </div>
              </ValerieCardField>
              <ValerieCardField label="Service Line" value={referral.serviceLine} />
              <ValerieCardField label="Scheduling Priority" value={referral.schedulingPriority} />
              <ValerieCardField label="Reason for Referral" value={referral.reasonForReferral} colSpan={3} />
            </ValerieCardSection>
            {(referral.authorizationNumber || authExpirationDate) && (
              <ValerieCardSection title="Authorization">
                <ValerieCardField label="Authorization Number" value={referral.authorizationNumber} />
                <ValerieCardField
                  label="Expiration Date"
                  value={authExpirationDate ? formatIsoDate(authExpirationDate) : null}
                />
              </ValerieCardSection>
            )}
            {referringFax && (
              <ValerieCardSection title="Referring Location">
                <ValerieCardField label="Fax" value={referringFax} />
              </ValerieCardSection>
            )}
            {hasDiagnoses && (
              <section className="space-y-2">
                <ValerieCardSectionTitle>ICD-10 Diagnosis List</ValerieCardSectionTitle>
                <DiagnosisListTable
                  diagnoses={diagnosisList}
                  fallbackDiagnosis={
                    referral.diagnosisCode || referral.diagnosisText
                      ? {
                          diagnosisCode: referral.diagnosisCode ?? null,
                          diagnosisText: referral.diagnosisText ?? null,
                        }
                      : null
                  }
                />
              </section>
            )}
          </ValerieCardSections>
        </ValerieCard>
      </ContextMenuTrigger>
      {effectiveIsEditable && <CardActions {...cardActionsProps} isContextMenu={true} />}
    </ContextMenu>
  );
}
